import React from 'react';
import { Box, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  TimeScale,
  Decimation,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend, TimeScale, Decimation);

const RANGE_OPTIONS = ['1h', '6h', '12h', '24h', '7d', '30d'];
const METRIC_OPTIONS = [
  { value: 'price', label: 'Price' },
  { value: 'marketCap', label: 'Market Cap' },
  { value: 'liquidity', label: 'Liquidity' },
];

const useStyles = makeStyles((theme) => ({
  panel: {
    background: 'rgba(10, 14, 30, 0.88)',
    border: '1px solid rgba(77, 121, 255, 0.22)',
    borderRadius: 14,
    padding: 14,
    textAlign: 'left',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: 12,
    },
  },
  title: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 10,
  },
  divider: {
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  selectorGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  selectorButton: {
    appearance: 'none',
    borderRadius: 999,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(12, 18, 38, 0.78)',
    color: '#cfd6ff',
    fontSize: 11,
    fontWeight: 800,
    padding: '6px 10px',
    cursor: 'pointer',
    transition: 'all 0.16s ease',
    outline: 'none',
    fontFamily: 'inherit',
    '&:focus-visible': {
      boxShadow: '0 0 0 2px rgba(77,121,255,0.45)',
    },
  },
  selectorButtonActive: {
    borderColor: 'rgba(77, 121, 255, 0.5)',
    background: 'rgba(77, 121, 255, 0.2)',
    color: '#ffffff',
    boxShadow: '0 0 16px rgba(77,121,255,0.16)',
  },
  chartWrap: {
    position: 'relative',
    width: '100%',
    height: 360,
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.16)',
    background: 'linear-gradient(180deg, rgba(18, 26, 50, 0.95), rgba(8, 12, 24, 0.95))',
    padding: 10,
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      height: 300,
      padding: 8,
    },
    [theme.breakpoints.down('xs')]: {
      height: 260,
    },
  },
  empty: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9ea4d8',
    fontSize: 13,
    textAlign: 'center',
    padding: 20,
    boxSizing: 'border-box',
  },
  footer: {
    color: '#9ea4d8',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 12,
  },
  error: {
    color: '#ffb4b4',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 8,
  },
}));

function formatCompactUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';

  if (n >= 1) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 2,
    }).format(n);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: n >= 0.01 ? 6 : 10,
  }).format(n);
}

function clampInt(value, min, max) {
  const n = Math.trunc(Number(value));
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}

function suggestUsdFractionDigits(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 2;
  if (n >= 1) return 2;

  const digits = Math.ceil(-Math.log10(n)) + 2;
  return clampInt(digits, 2, 10);
}

function formatFixedUsd(value, fractionDigits) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';

  if (n >= 1) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(n);
  }

  const digits = clampInt(fractionDigits, 2, 10);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);
}

function getMetricConfig(metric) {
  if (metric === 'marketCap') {
    return {
      label: 'Market Cap',
      color: '#9ef7c5',
      fill: 'rgba(158, 247, 197, 0.16)',
      accessor: (row) => Number(row && row.market_cap_usd),
    };
  }
  if (metric === 'liquidity') {
    return {
      label: 'Liquidity',
      color: '#ffcc80',
      fill: 'rgba(255, 204, 128, 0.16)',
      accessor: (row) => Number(row && row.liquidity_usd),
    };
  }
  return {
    label: 'Price',
    color: '#4d79ff',
    fill: 'rgba(77, 121, 255, 0.18)',
    accessor: (row) => Number(row && row.price_usd),
  };
}

function parseTimeValue(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return Number.isFinite(ts.getTime()) ? ts : null;
  if (typeof ts === 'number') return Number.isFinite(ts) ? new Date(ts) : null;
  const parsed = new Date(ts);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function getRangeScaleConfig(range) {
  const currentRange = String(range || '24h').toLowerCase();

  if (currentRange === '1h') {
    return {
      unit: 'minute',
      maxTicksLimit: 6,
      displayFormats: {
        minute: 'h:mm a',
        hour: 'h a',
      },
    };
  }

  if (currentRange === '6h' || currentRange === '12h' || currentRange === '24h') {
    return {
      unit: 'hour',
      maxTicksLimit: 8,
      displayFormats: {
        minute: 'h:mm a',
        hour: 'h a',
      },
    };
  }

  if (currentRange === '7d') {
    return {
      unit: 'day',
      maxTicksLimit: 8,
      displayFormats: {
        day: 'MMM d',
        week: 'MMM d',
      },
    };
  }

  if (currentRange === '30d') {
    return {
      unit: 'day',
      maxTicksLimit: 10,
      displayFormats: {
        day: 'MMM d',
        week: 'MMM d',
        month: 'MMM d',
      },
    };
  }

  return {
    unit: 'hour',
    maxTicksLimit: 8,
    displayFormats: {
      minute: 'h:mm a',
      hour: 'h a',
      day: 'MMM d',
    },
  };
}

function useSeries(metric, priceHistory, liquidityHistory) {
  return React.useMemo(() => {
    const metricConfig = getMetricConfig(metric);
    const rawPoints = metric === 'liquidity'
      ? (Array.isArray(liquidityHistory && liquidityHistory.points) ? liquidityHistory.points : [])
      : (Array.isArray(priceHistory && priceHistory.points) ? priceHistory.points : []);

    const points = rawPoints
      .map((row) => ({
        x: parseTimeValue(row && row.ts ? row.ts : null),
        y: metricConfig.accessor(row),
      }))
      .filter((row) => row.x && Number.isFinite(row.y));

    return {
      points,
      metricConfig,
      lastPoint: points.length ? points[points.length - 1] : null,
    };
  }, [metric, priceHistory, liquidityHistory]);
}

export default function ChartPanel({
  priceHistory,
  liquidityHistory,
  loading,
  error,
  range,
  metric,
  onRangeChange,
  onMetricChange,
}) {
  const classes = useStyles();
  const currentMetric = String(metric || 'price');
  const currentRange = String(range || '24h').toLowerCase();
  const { points, metricConfig, lastPoint } = useSeries(currentMetric, priceHistory, liquidityHistory);

  const priceFractionDigits = React.useMemo(() => {
    if (currentMetric !== 'price') return 2;
    const reference = lastPoint && Number.isFinite(lastPoint.y) ? lastPoint.y : null;
    if (reference && reference > 0) return suggestUsdFractionDigits(reference);

    const sample = points.find((row) => Number.isFinite(row && row.y) && row.y > 0);
    return sample ? suggestUsdFractionDigits(sample.y) : 2;
  }, [currentMetric, lastPoint, points]);

  const handleMetricChange = React.useCallback((nextMetric) => {
    if (nextMetric === currentMetric) return;
    if (typeof onMetricChange === 'function') onMetricChange(nextMetric);
  }, [currentMetric, onMetricChange]);

  const handleRangeChange = React.useCallback((nextRange) => {
    if (nextRange === currentRange) return;
    if (typeof onRangeChange === 'function') onRangeChange(nextRange);
  }, [currentRange, onRangeChange]);

  const chartData = React.useMemo(() => ({
    datasets: [
      {
        label: metricConfig.label,
        data: points,
        parsing: false,
        borderColor: metricConfig.color,
        backgroundColor: metricConfig.fill,
        borderWidth: 2,
        pointRadius: points.length > 100 ? 0 : 1.5,
        pointHoverRadius: 4,
        pointBackgroundColor: metricConfig.color,
        fill: true,
        tension: 0.28,
      },
    ],
  }), [metricConfig, points]);

  const chartOptions = React.useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    normalized: true,
    parsing: false,
    interaction: {
      mode: 'nearest',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      decimation: {
        enabled: points.length > 250,
        algorithm: 'lttb',
        samples: 120,
      },
      tooltip: {
        backgroundColor: 'rgba(8, 12, 24, 0.96)',
        borderColor: 'rgba(77, 121, 255, 0.28)',
        borderWidth: 1,
        titleColor: '#ffffff',
        bodyColor: '#d9defa',
        displayColors: false,
        callbacks: {
          label: (context) => {
            const value = context && context.parsed ? context.parsed.y : 0;
            const formatted = currentMetric === 'price'
              ? formatFixedUsd(value, priceFractionDigits)
              : formatCompactUsd(value);
            return `${metricConfig.label}: ${formatted}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        min: points.length ? points[0].x : undefined,
        max: points.length ? points[points.length - 1].x : undefined,
        time: {
          unit: getRangeScaleConfig(currentRange).unit,
          displayFormats: getRangeScaleConfig(currentRange).displayFormats,
          tooltipFormat: 'MMM d, yyyy HH:mm',
        },
        grid: {
          color: 'rgba(255,255,255,0.06)',
        },
        ticks: {
          color: '#9ea4d8',
          maxTicksLimit: getRangeScaleConfig(currentRange).maxTicksLimit,
          autoSkip: true,
        },
      },
      y: {
        grid: {
          color: 'rgba(255,255,255,0.06)',
        },
        ticks: {
          color: '#9ea4d8',
          callback: (value) => (currentMetric === 'price'
            ? formatFixedUsd(value, priceFractionDigits)
            : formatCompactUsd(value)),
        },
      },
    },
  }), [currentMetric, currentRange, metricConfig, points, priceFractionDigits]);

  return (
    <Box className={classes.panel}>
      <Box className={classes.toolbar}>
        <Box className={classes.selectorGroup}>
          {METRIC_OPTIONS.map((option) => (
            <Box
              key={option.value}
              component="button"
              type="button"
              className={`${classes.selectorButton} ${currentMetric === option.value ? classes.selectorButtonActive : ''}`}
              onClick={() => handleMetricChange(option.value)}
              aria-pressed={currentMetric === option.value}
            >
              {option.label}
            </Box>
          ))}
        </Box>

        <Box className={classes.selectorGroup}>
          {RANGE_OPTIONS.map((option) => (
            <Box
              key={option}
              component="button"
              type="button"
              className={`${classes.selectorButton} ${currentRange === option ? classes.selectorButtonActive : ''}`}
              onClick={() => handleRangeChange(option)}
              aria-pressed={currentRange === option}
            >
              {String(option).toUpperCase()}
            </Box>
          ))}
        </Box>
      </Box>

      <Box className={classes.chartWrap}>
        {loading ? (
          <Box className={classes.empty}>Loading {metricConfig.label.toLowerCase()} chart…</Box>
        ) : error && !points.length ? (
          <Box className={classes.empty}>{error}</Box>
        ) : !points.length ? (
          <Box className={classes.empty}>No {metricConfig.label.toLowerCase()} history available for the selected range.</Box>
        ) : (
          <Line data={chartData} options={chartOptions} />
        )}
      </Box>

      <Typography className={classes.footer}>
        {lastPoint
          ? `Latest ${metricConfig.label.toLowerCase()} ${currentMetric === 'price' ? formatFixedUsd(lastPoint.y, priceFractionDigits) : formatCompactUsd(lastPoint.y)} • ${lastPoint.x}`
          : `Showing ${metricConfig.label.toLowerCase()} history for ${String(currentRange).toUpperCase()}.`}
      </Typography>
      {error && points.length ? <Typography className={classes.error}>{error}</Typography> : null}
    </Box>
  );
}
